import { useEffect, useState, useCallback } from "react";

/**
 * Runs an async loader and tracks { data, loading, error }.
 * Used by public pages to read from the data repository (Firestore) with
 * proper loading / empty / error states.
 *
 * @param {() => Promise<any>} loader  async function returning the data
 * @param {Array} deps                 dependency list that re-triggers the load
 * @param {any} initial                initial data value (default [])
 */
export function useAsyncData(loader, deps = [], initial = []) {
  const [state, setState] = useState({ data: initial, loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(() => loader())
      .then((data) => {
        if (alive) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("[useAsyncData] load failed:", error);
        if (alive) setState({ data: initial, loading: false, error });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload };
}
